import React, { useState } from 'react';
import { Icon } from '../../Icon';
import { maskNumber, maskInteger, parseBrazilianNumber } from '../../../utils/formatUtils';

export const FormField: React.FC<{
  label: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'select' | 'textarea' | 'number' | 'date';
  required?: boolean;
  helpText?: string;
  options?: { value: string; label: string }[];
  isNumeric?: boolean;
  isInteger?: boolean;
}> = ({
  label,
  placeholder,
  value,
  onChange,
  type = 'text',
  required = false,
  helpText,
  options,
  isNumeric = false,
  isInteger = false
}) => (
    <div>
      <label className="text-xs font-black text-theme-muted uppercase tracking-widest flex items-center gap-1 mb-2">
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      {type === 'textarea' ? (
        <textarea
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-4 py-3 bg-theme-input border border-theme-border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-theme-text resize-none"
          rows={4}
        />
      ) : type === 'select' ? (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-4 py-3 bg-theme-input border border-theme-border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-theme-text"
        >
          {options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={(isNumeric || isInteger) ? 'text' : type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            let val = e.target.value;
            if (isNumeric && (type === 'text' || type === 'number')) {
              val = maskNumber(val);
            } else if (isInteger && (type === 'text' || type === 'number')) {
              val = maskInteger(val);
            }
            if (e.target.value !== val) {
              e.target.value = val;
            }
            onChange(val);
          }}
          className="w-full px-4 py-3 bg-theme-input border border-theme-border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-theme-text"
        />
      )}
      {helpText && (
        <p className="text-xs text-theme-muted mt-1">{helpText}</p>
      )}
    </div>
  );

export const ResultCard: React.FC<{
  title: string;
  value: string | number;
  description?: string;
  color?: 'indigo' | 'emerald' | 'rose' | 'amber' | 'slate' | 'blue' | 'green' | 'purple' | 'orange' | 'red';
}> = ({
  title,
  value,
  description,
  color = 'indigo',
}) => {
    const colorClasses = {
      indigo: 'bg-accent-gradient text-white shadow-accent',
      emerald: 'bg-emerald-600 text-white shadow-emerald-200',
      rose: 'bg-rose-600 text-white shadow-rose-200',
      amber: 'bg-amber-600 text-white shadow-amber-200',
      slate: 'bg-slate-600 text-white shadow-slate-200',
      blue: 'bg-blue-600 text-white shadow-blue-200',
      green: 'bg-green-600 text-white shadow-green-200',
      purple: 'bg-purple-600 text-white shadow-purple-200',
      orange: 'bg-orange-600 text-white shadow-orange-200',
      red: 'bg-red-600 text-white shadow-red-200'
    };

    return (
      <div className={`p-8 rounded-[2.5rem] shadow-xl text-center ${colorClasses[color]}`}>
        <p className="text-xs font-black opacity-60 uppercase tracking-widest mb-2">{title}</p>
        <p className="text-4xl font-[1000] mb-3">{value}</p>
        {description && (
          <p className="text-sm opacity-90">{description}</p>
        )}
      </div>
    );
  };

export const EducationalBlock = ({ title, tips }: { title: string; tips: string[] }) => (
  <div className="bg-theme-muted-section p-8 rounded-3xl border border-theme-border mt-8">
    <h3 className="text-xl font-black mb-6 flex items-center gap-3">
      <Icon name="info" className="w-5 h-5 text-theme-muted" />
      {title}
    </h3>
    <ul className="space-y-3">
      {tips.map((tip, index) => (
        <li key={index} className="flex items-start gap-3">
          <Icon name="check-circle" className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
          <span className="text-theme-text">{tip}</span>
        </li>
      ))}
    </ul>
  </div>
);
